import { useCallback, useEffect, useRef, useState } from 'react';
import { chatService, DM_READ_EVENT } from '../services/chat';
import { ChatMessage, ConversationSummary } from '../types/chat';
import { useMessagesRealtime } from './useMessagesRealtime';

export function useConversations(enabled: boolean, currentUserId?: string) {
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const refresh = useCallback(async () => {
    if (!enabled) {
      setConversations([]);
      setLoading(false);
      return;
    }
    try {
      const rows = await chatService.listConversations();
      if (!mountedRef.current) return;
      setConversations(rows);
      setError(null);
    } catch (err) {
      if (!mountedRef.current) return;
      setError(err instanceof Error ? err.message : 'Could not load your messages');
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [enabled]);

  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;

  useEffect(() => {
    setLoading(enabled);
    refresh();
  }, [enabled, refresh]);

  // Patch the list in place rather than refetching on every message; only a
  // thread we haven't seen yet needs a round trip.
  const applyIncoming = useCallback(
    (message: ChatMessage) => {
      setConversations(prev => {
        const index = prev.findIndex(c => c.conversation_id === message.conversation_id);
        if (index === -1) {
          refreshRef.current();
          return prev;
        }

        const existing = prev[index];
        const fromMe = message.sender_id === currentUserId;
        const updated: ConversationSummary = {
          ...existing,
          last_message_at: message.created_at,
          last_message_body: message.body,
          last_message_sender_id: message.sender_id,
          last_message_attachment_type: message.attachment_type,
          unread_count: fromMe ? existing.unread_count : existing.unread_count + 1,
        };

        // The list is ordered by most recent activity.
        const next = prev.filter((_, i) => i !== index);
        return [updated, ...next];
      });
    },
    [currentUserId]
  );

  useMessagesRealtime(enabled, applyIncoming, () => refreshRef.current());

  // Clear the badge for a thread the moment it's marked read.
  useEffect(() => {
    if (!enabled) return;
    const handler = (event: Event) => {
      const conversationId = (event as CustomEvent<{ conversationId?: string }>).detail
        ?.conversationId;
      if (!conversationId) return;
      setConversations(prev =>
        prev.map(c =>
          c.conversation_id === conversationId && c.unread_count > 0
            ? { ...c, unread_count: 0 }
            : c
        )
      );
    };
    window.addEventListener(DM_READ_EVENT, handler);
    return () => window.removeEventListener(DM_READ_EVENT, handler);
  }, [enabled]);

  return { conversations, loading, error, refresh, setConversations };
}
